import {
    ArrowRightIcon,
    Button,
    ExpandableText,
    Heading,
    IncrementerButton,
    InfoIcon,
    OptimizedImage,
    Tag,
    getOptimizedImagePathFromUrl,
    style,
} from "@r360/library";
import classNames from "classnames";
import cloneDeep from "lodash/cloneDeep";
import groupBy from "lodash/groupBy";
import sum from "lodash/sum";
import sumBy from "lodash/sumBy";
import React, { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AxiosClient from "../../API/AxiosClient";
import { hasBookstat, isBooked, isNew, isSkipass, sortBySortorder } from "../../BusinessUtils";
import { formatPrice } from "../../Helper";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import useMemoizeArg from "../../hooks/useMemoizeArg";
import useTranslate from "../../hooks/useTranslate";
import {
    currencySelector,
    reservationTypeResultSelector,
    resvTypeByLid,
    userSelector,
} from "../../selectors/Selectors";
import { setReservationTypeResults } from "../../store/actions/reservationResult";
import { TCartItem, TGroup, TItem, TReservationResult } from "../../store/types";
import { PromotionCode } from "../PromotionCode";
import "./AccommodationSkipassExtra.scss";

export type TAddToCartData = {
    item: TItem | TCartItem;
    quantity: number;
};

type AccommodationSkipassProps = {
    accommodation: TItem;
    guests: number[];
    skipassExtraResvTypeId?: number;
    skipassExtraGroup: TGroup;
    onChange: (addToCartData: TAddToCartData[]) => void;
    accommodationConnectedProductsInCart: TCartItem[];
};

export const AccommodationSkipassExtraCard = ({
    accommodation,
    guests,
    skipassExtraResvTypeId,
    skipassExtraGroup,
    onChange,
    accommodationConnectedProductsInCart = [],
}: AccommodationSkipassProps) => {
    const navigate = useNavigate();
    const t = useTranslate();
    const lang = useAppSelector(state => state.clientData?.lang);
    const currency = useAppSelector(currencySelector);
    const allowNumberOfGuestsChange = useAppSelector(
        state => state.clientData?.options?.reservation.allow_change_guests
    );
    const skipassExtraReservationResult = useAppSelector(state =>
        reservationTypeResultSelector(state, skipassExtraResvTypeId)
    );
    const useCampaignForSkipassExtra = !!useAppSelector(
        state => resvTypeByLid(state, skipassExtraResvTypeId)?.useCampaign
    );

    const [summaryPrice, setSummaryPrice] = useState(0);
    const [summaryOrdinaryPrice, setSummaryOrdinaryPrice] = useState(0);
    const [isMax, setIsMax] = useState(false);
    const [addToCartData, setAddToCartData] = useState<TAddToCartData[]>([]);
    const [bookedQuantites, setBookedQuantities] = useState<Record<number, number>>({});

    const skipassExtraHasPromotionCode = skipassExtraReservationResult?.search?.promoCode;

    useEffect(() => {
        if (!skipassExtraGroup) return;

        // Check if there are skipass extras connected to the accommodation in cart.
        if (addToCartData.length === 0) {
            // If so, add those items to the state.
            setAddToCartData(
                cloneDeep(accommodationConnectedProductsInCart)
                    .filter(item => !hasBookstat(item))
                    .filter(item => item.count >= 1)
                    .map(item => ({ item, quantity: item.count }))
            );
        } else {
            // Update the items in the state with the latest search data.
            setAddToCartData(prev => {
                return prev.map(addToCartDataEntry => {
                    // Keep connectedProductId and cartItemId.
                    const connectedProductId = addToCartDataEntry.item.connectedProductId;
                    const cartItemId = (addToCartDataEntry.item as TCartItem).cartItemId;

                    const updatedItem = skipassExtraGroup.items.find(
                        item => item.poollid === addToCartDataEntry.item.poollid
                    );

                    return {
                        ...addToCartDataEntry,
                        item: { ...addToCartDataEntry.item, ...updatedItem, cartItemId, connectedProductId },
                    };
                });
            });
        }
        // eslint-disable-next-line
    }, [useMemoizeArg(skipassExtraGroup)]);

    useEffect(() => {
        setBookedQuantities(
            Object.entries(groupBy(accommodationConnectedProductsInCart.filter(isBooked), item => item.poollid)).reduce(
                (acc, [poollid, items]) => {
                    acc[parseInt(poollid, 10)] = sumBy(items, "bookedquantity");
                    return acc;
                },
                {} as Record<number, number>
            )
        );

        // eslint-disable-next-line
    }, [useMemoizeArg(accommodationConnectedProductsInCart)]);

    useEffect(() => {
        setSummaryPrice(
            sumBy(addToCartData, addToCartDataEntry => addToCartDataEntry.quantity * addToCartDataEntry.item.aprice)
        );

        setSummaryOrdinaryPrice(
            sumBy(
                addToCartData,
                addToCartDataEntry =>
                    addToCartDataEntry.quantity *
                    (addToCartDataEntry.item.ordinaryprice > 0
                        ? addToCartDataEntry.item.ordinaryprice
                        : addToCartDataEntry.item.price)
            )
        );

        setIsMax(
            sumBy(addToCartData, addToCartDataEntry => addToCartDataEntry.quantity) +
                sum(Object.values(bookedQuantites)) ===
                guests.length
        );
    }, [addToCartData, guests, bookedQuantites]);

    useEffect(() => {
        onChange(addToCartData);
        // eslint-disable-next-line
    }, [useMemoizeArg(addToCartData)]);

    const onIncrementClick = (poollid: number) => {
        const item = skipassExtraGroup?.items.find(item => item.poollid === poollid);

        if (!item || isMax) return;

        const addToCartDataIndex = addToCartData.findIndex(entry => entry.item.poollid === poollid);

        if (addToCartDataIndex >= 0) {
            const addToCartDataClone = cloneDeep(addToCartData);
            addToCartDataClone[addToCartDataIndex].quantity += 1;

            setAddToCartData(addToCartDataClone);
        } else {
            setAddToCartData([
                ...addToCartData,
                { item: { ...item, groupTitle: skipassExtraGroup?.groupitem.title || "" }, quantity: 1 },
            ]);
        }
    };

    const onDecrementClick = (poollid: number) => {
        setAddToCartData(
            cloneDeep(addToCartData).reduce((acc: TAddToCartData[], curr) => {
                if (curr.item.poollid === poollid && curr.quantity >= 1) {
                    curr.quantity -= 1;
                }

                acc.push(curr);

                return acc;
            }, [])
        );
    };

    if (!skipassExtraGroup?.groupitem || skipassExtraGroup.items.length === 0) return null;

    return (
        <section>
            <div>
                <Heading type="h2" styleAs="h5" className="u-mb-12">
                    {t("book.accommodation_extras_skipass_headline")}
                </Heading>
            </div>
            <div className="accommodation-skipass-extra">
                <Row>
                    <Col md={{ span: 8, order: 1 }} xs={{ span: 12, order: 2 }}>
                        <div className="u-pl-18 u-pr-18 u-pt-18">
                            <Heading type="h3" styleAs="h6" className="accommodation-skipass-extra__first-heading">
                                {skipassExtraGroup.groupitem.category}
                            </Heading>
                            <Heading type="h4" styleAs="h3">
                                {skipassExtraGroup.groupitem.title}
                            </Heading>
                            {skipassExtraGroup.groupitem.weblong && (
                                <ExpandableText
                                    text={skipassExtraGroup.groupitem.weblong}
                                    collapsedButtonText={t("book.general.show_more")}
                                    expandedButtonText={t("book.general.show_less")}
                                    collapsedMaxLength={140}
                                    inlineButton
                                />
                            )}
                            <div className="u-mt-30">
                                {sortBySortorder(skipassExtraGroup.items.filter(isSkipass)).map((item: TItem) => (
                                    <SkipassVariant
                                        key={item.id}
                                        type={item.title || item.agecategory || ""}
                                        age={`${item.minage} - ${item.maxage} ${t("book.general.years")}`}
                                        quantity={
                                            addToCartData.find(
                                                addToCartDataEntry => addToCartDataEntry.item.poollid === item.poollid
                                            )?.quantity || 0
                                        }
                                        bookedQuantity={bookedQuantites[item.poollid] || 0}
                                        currency={currency}
                                        price={item.price}
                                        ordinaryPrice={item.ordinaryprice}
                                        increment={() => onIncrementClick(item.poollid)}
                                        decrement={() => onDecrementClick(item.poollid)}
                                        isMax={isMax}
                                    />
                                ))}
                            </div>
                            <div className="accommodation-skipass-extra__total-price">
                                <p className="u-mb-0">
                                    {t("book.cart.summary.total_price")}:
                                    <span className="accommodation-skipass-extra__discount-price">
                                        {formatPrice(summaryPrice, currency)}
                                    </span>
                                    {summaryOrdinaryPrice > 0 && summaryOrdinaryPrice !== summaryPrice && (
                                        <span className="accommodation-skipass-extra__ord-price">
                                            {formatPrice(summaryOrdinaryPrice, currency)}
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </Col>
                    <Col md={{ span: 4, order: 2 }} xs={{ span: 12, order: 1 }}>
                        {skipassExtraGroup.groupitem.thumbnail?.url && (
                            <div className="accommodation-skipass-extra__img">
                                <OptimizedImage
                                    path={getOptimizedImagePathFromUrl(skipassExtraGroup.groupitem.thumbnail.url)}
                                    altText={
                                        skipassExtraGroup.groupitem?.thumbnail?.translations?.[lang]?.accessibility ??
                                        ""
                                    }
                                />
                            </div>
                        )}
                        <div className="accommodation-skipass-extra__tag-wrapper">
                            {t("book.accommodation_extras_skipass_discount_tag") && (
                                <Tag text={t("book.accommodation_extras_skipass_discount_tag")} theme="success-light" />
                            )}
                            {skipassExtraHasPromotionCode && (
                                <Tag text={t("book.general.campaign")} theme="yellow" className="u-mb-12" />
                            )}
                        </div>
                        {skipassExtraResvTypeId && useCampaignForSkipassExtra && (
                            <SkipassPromotionCode
                                skipassExtraResvTypeId={skipassExtraResvTypeId}
                                skipassExtraReservationResult={skipassExtraReservationResult}
                                skipassExtraGroup={skipassExtraGroup}
                                skipassesFromCart={accommodationConnectedProductsInCart}
                            />
                        )}
                    </Col>
                    <Col md={{ span: 12, order: 3 }} xs={{ span: 12, order: 3 }}>
                        {isMax && (
                            <div className="u-px-18 u-mb-12">
                                <InfoIcon color={style.yellowColor} className="u-mr-6" />
                                <span className="accommodation-skipass-extra__max-info-text">
                                    {t("book.accommodation_extras_skipass_max_message")}
                                </span>
                                {!isNew(accommodation) &&
                                    !!accommodation.capac &&
                                    guests.length < accommodation.capac &&
                                    allowNumberOfGuestsChange && (
                                        <Button
                                            type="tertiary"
                                            rightIcon={<ArrowRightIcon />}
                                            buttonClassName="u-ml-12"
                                            onClick={() => navigate("/cart")}
                                        >
                                            {t("book.accommodation_extras.add_guests")}
                                        </Button>
                                    )}
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
        </section>
    );
};

type TSkipassVariant = {
    type: string;
    age: string;
    quantity: number;
    bookedQuantity?: number;
    price: number;
    ordinaryPrice: number;
    currency: string;
    isMax: boolean;
    increment: () => void;
    decrement: () => void;
};

const SkipassVariant = ({
    type,
    age,
    quantity,
    bookedQuantity = 0,
    price,
    ordinaryPrice,
    currency,
    isMax,
    increment,
    decrement,
}: TSkipassVariant) => {
    const { isMobile } = useAppSelector(state => state.window);

    return (
        <Row className="u-mt-12 u-d-flex u-align-items-center u-justify-content-between">
            <Col xs={4} sm={5}>
                <span className="u-fw-medium">{type}, </span>
                {isMobile && <br />}
                <span>{age}</span>
            </Col>
            <Col xs={3}>
                <div className="u-d-flex align-items-center">
                    <IncrementerButton
                        type="decrement"
                        incrementerSize="small"
                        disabled={quantity <= 0}
                        onClick={decrement}
                    />

                    <span className="accommodation-skipass-extra__quantity">{quantity + bookedQuantity}</span>
                    <IncrementerButton type="increment" incrementerSize="small" disabled={isMax} onClick={increment} />
                </div>
            </Col>
            <Col xs={4}>
                <span className={classNames("u-fw-medium", { "u-color-bright-blue": ordinaryPrice > 0 })}>
                    {formatPrice(price, currency)}
                </span>
                {isMobile && <br />}
                {ordinaryPrice > 0 && (
                    <span style={{ textDecoration: "line-through", marginLeft: isMobile ? "0px" : "12px" }}>
                        {formatPrice(ordinaryPrice, currency)}
                    </span>
                )}
            </Col>
        </Row>
    );
};

type TSkipassPromotionCode = {
    skipassExtraResvTypeId: number;
    skipassExtraReservationResult?: TReservationResult;
    skipassExtraGroup: TGroup;
    skipassesFromCart: TCartItem[];
};

const SkipassPromotionCode = ({
    skipassExtraResvTypeId,
    skipassExtraReservationResult,
    skipassExtraGroup,
    skipassesFromCart,
}: TSkipassPromotionCode) => {
    const t = useTranslate();
    const dispatch = useAppDispatch();
    const isLoggedIn = !!useAppSelector(userSelector);
    const reservationType = useAppSelector(state => resvTypeByLid(state, skipassExtraResvTypeId));
    const [promotionCode, setPromotionCode] = useState<{ code: string; validated: boolean }>({
        code: "",
        validated: false,
    });
    const [searching, setSearching] = useState(false);
    const [skipassNotInCampaign, setSkipassNotInCampaign] = useState(false);
    const [promotionCodeError, setPromotionCodeError] = useState(false);
    const promoCodeFromSearch = skipassExtraReservationResult?.search?.promoCode;
    const newSkipassesFromCart = skipassesFromCart.filter(item => !isBooked(item));

    // If a all skipass extras in the cart have the same promotion code, get that code. Use new skipasses if any exists.
    const promoCodeFromCart = (newSkipassesFromCart.length > 0 ? newSkipassesFromCart : skipassesFromCart)
        .map(item => item.promotionCode)
        .reduce((_acc, code, _i, arr) => (arr.every(c => c === code) ? code : undefined), undefined);

    const isCodesEqual = (code1?: string, code2?: string) =>
        (code1 ?? "").toLowerCase() === (code2 ?? "").toLowerCase();

    const handleSearchWithPromotionCode = useCallback(
        async (code: string) => {
            let validatedPromotionCode = "";

            // Validate promotion code if any.
            if (code) {
                try {
                    const response = await AxiosClient.post("/promotions", { promoCode: code });

                    if (response) {
                        setPromotionCodeError(false);
                        validatedPromotionCode = response.data.payload.promotionCode;
                    }
                } catch (error) {
                    setPromotionCodeError(true);
                    setSearching(false);
                    return;
                }
            }

            const { arrdate, depdate } = skipassExtraReservationResult?.search || {};

            const requestData = {
                startdate: arrdate,
                enddate: depdate,
                promoCode: validatedPromotionCode,
                filterOnDays: true,
            };

            AxiosClient.get(`/search?resvtype=${skipassExtraResvTypeId}`, {
                params: requestData,
            }).then(response => {
                // If the skipass is in the campaign, update the reservationtype result.
                const skipassIsInCampaign = Object.values(
                    response.data.payload.groups as { [key: string]: TGroup }
                ).some(group => group.groupitem?.grouppoollid === skipassExtraGroup.groupitem?.grouppoollid);

                if (skipassIsInCampaign) {
                    dispatch(
                        setReservationTypeResults(
                            response.data.payload,
                            reservationType,
                            validatedPromotionCode,
                            true,
                            "",
                            "",
                            false,
                            isLoggedIn,
                            false
                        )
                    );

                    setSearching(false);
                    setPromotionCode({ code: validatedPromotionCode, validated: true });
                } else {
                    setSkipassNotInCampaign(true);
                }

                setSearching(false);
            });
        },
        [
            skipassExtraReservationResult?.search,
            skipassExtraResvTypeId,
            skipassExtraGroup.groupitem?.grouppoollid,
            dispatch,
            isLoggedIn,
        ]
    );

    useEffect(() => {
        if (promoCodeFromCart) {
            if (isCodesEqual(promoCodeFromCart, promoCodeFromSearch)) {
                setPromotionCode({ code: promoCodeFromCart, validated: true });
                return;
            }

            // If there is a code from the items in cart, use that code in search.
            setPromotionCode({ code: promoCodeFromCart, validated: false });
            setSearching(true);
        } else if (promoCodeFromSearch && !promoCodeFromCart) {
            if (isCodesEqual(promoCodeFromSearch, promotionCode.code)) {
                return;
            }

            // Reset the code if there is a code from search but not from cart.
            setPromotionCode({ code: "", validated: false });
            setSearching(true);
        }
        // eslint-disable-next-line
    }, [promoCodeFromCart]);

    useEffect(() => {
        if (promotionCodeError) {
            setPromotionCode({ code: "", validated: false });
        }
    }, [promotionCodeError]);

    useEffect(() => {
        if (searching && promotionCode.code !== undefined) {
            handleSearchWithPromotionCode(promotionCode.code);
        }
        // eslint-disable-next-line
    }, [promotionCode, searching]);

    const handleSavePromotionCode = (promotionCode: string) => {
        setPromotionCode({ code: promotionCode, validated: false });
        setPromotionCodeError(false);
        setSkipassNotInCampaign(false);
        setSearching(true);
    };

    return (
        <div className="u-p-18 u-pl-lg-0">
            <PromotionCode
                promotionCode={promotionCode.validated ? promotionCode.code : ""}
                validating={searching}
                onCodeSave={handleSavePromotionCode}
                promotionCodeError={promotionCodeError}
                onDarkBackground={false}
                alwaysShowContent
                fullWidth
                className="accommodation-skipass-extra__promotion-code"
                autoFocus={false}
            />
            {skipassNotInCampaign && (
                <p className="u-mt-12 u-color-alert">{t("book.accommodation_extras_skipass_not_in_campaign")}</p>
            )}
        </div>
    );
};
